import { createContext, ReactNode, useContext, useState } from 'react'

import { Notification } from '@/Components/Common/Notifications/types'
import { User } from '@/Components/Pages/Accounts/types'
import { BreadcrumbItem } from '@/Pages/types'
import { FCWithChildren } from '@/types'

import App from '@/Components/App'
import ErrorBoundary from '@/Components/Common/ErrorBoundary'
import { Environments } from '@/Components/EnvBanner'
import Layout from '@/Components/Layout/Layout'

export type PageProviderProps = {
  title?: string
  action?: ReactNode
  currentUser: User
  modelName?: 'user' | 'assessment' | 'certification letter'
  env: Environments
  showingPageModal?: boolean
  setShowingPageModal?: React.Dispatch<React.SetStateAction<boolean>>
  breadcrumbs?: BreadcrumbItem[]
}

export type PageContextType = {
  addingRecord: boolean
  setAddingRecord: (adding: boolean) => void
  currentUser: User
  setCurrentUser: (user: User) => void
  notifications: Notification[]
  setNotifications: (notifications: Notification[]) => void
  showingPageModal?: boolean
  setShowingPageModal?: React.Dispatch<React.SetStateAction<boolean>>
  breadcrumbs: BreadcrumbItem[]
}

const PageContext = createContext<PageContextType | undefined>(undefined)

export const usePage = () => {
  const context = useContext(PageContext)
  if (!context) throw new Error('usePage must be used within a PageProvider')
  return context
}

const PageProvider: FCWithChildren<PageProviderProps> = ({
  children,
  currentUser: initialUser,
  env,
  showingPageModal,
  setShowingPageModal,
  breadcrumbs = [],
  ...props
}) => {
  const [currentUser, setCurrentUser] = useState<User>(initialUser)
  const [addingRecord, setAddingRecord] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  return (
    <App env={env}>
      <ErrorBoundary>
        <PageContext.Provider
          value={{
            currentUser,
            setCurrentUser,
            addingRecord,
            setAddingRecord,
            notifications,
            setNotifications,
            showingPageModal,
            setShowingPageModal,
            breadcrumbs
          }}
        >
          <Layout {...props}>{children}</Layout>
        </PageContext.Provider>
      </ErrorBoundary>
    </App>
  )
}

export default PageProvider
